/**
 * Centralized TanStack Query key factory for the admin console (mirrors the
 * web/src/api/queryKeys.ts structure).
 *
 * Keys are hierarchical so invalidation can be scoped (e.g. invalidate every
 * query for one account, or the whole admin surface). All query hooks use
 * these factories — never inline string keys. Params are JSON-serializable.
 */
export const adminKeys = {
  /** Operator identity/role (GET /v1/admin/me). */
  me: ['admin', 'me'] as const,
  lookup: {
    all: ['admin', 'lookup'] as const,
    /** One lookup by raw query string. */
    query: (q: string) => [...adminKeys.lookup.all, q] as const,
  },
  accounts: {
    all: ['admin', 'accounts'] as const,
    /** Account detail (GET /v1/admin/accounts/{id}). */
    detail: (accountID: string) => [...adminKeys.accounts.all, accountID] as const,
    /** Entitlement audit trail. */
    entitlementAudit: (accountID: string, limit: number) =>
      [...adminKeys.accounts.detail(accountID), 'entitlement-audit', limit] as const,
    /** Billing audit trail. */
    billingAudit: (accountID: string, limit: number) =>
      [...adminKeys.accounts.detail(accountID), 'billing-audit', limit] as const,
    /** Usage (current + history) with granularity/days params. */
    usage: (
      accountID: string,
      params?: { granularity?: string; days?: number },
    ) => [...adminKeys.accounts.detail(accountID), 'usage', params ?? {}] as const,
    /** API keys for an account. */
    apiKeys: (accountID: string) =>
      [...adminKeys.accounts.detail(accountID), 'api-keys'] as const,
  },
  actions: {
    all: ['admin', 'actions'] as const,
    /** Operator action log, optionally filtered by account. */
    list: (params?: { account_id?: string; limit?: number }) =>
      [...adminKeys.actions.all, 'list', params ?? {}] as const,
  },
} as const;

export type AdminQueryKeyFactory = typeof adminKeys;
